import { APP_INITIALIZER, inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { SplashComponent } from './framework/splash/splash.component';
import { AuthorizationModule } from './authorization/authorization.module';
import { NavigationStart, Router, RouteReuseStrategy } from '@angular/router';
import { SharedModule } from './shared/shared.module';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { AppStoreModule } from './store/app-store.module';
import { OrganizationService } from './development/organization.service';
import { ToastrModule } from 'ngx-toastr';
import { SharedService } from './development/shared.service';
import { LetDirective, PushPipe } from '@ngrx/component';
import { UiKitModule } from './shared/ui-kit/ui-kit.module';
import { AlertEventsService } from './development/alert-events.service';
import { AppSettingsModule } from './settings/app-settings.module';
import { SearchConfigurationService } from './development/search-configuration.service';
import { AlertMonitoringService } from './development/alert-monitoring.service';
import { CameraService } from './development/camera.service';
import { CameraSearchSettingsService } from './development/camera-search-settings.service';
import { NoAuthComponent } from './pages/no-auth/no-auth.component';
import { GrantedAccessComponent } from './pages/granted-access/granted-access.component';
import { GrantedAccessService } from './development/granted-access.service';
import { StatsService } from './development/stats.service';
import { provideShareOrganizationSettings } from '@share-organization-settings';
import { ShareUserAccountSettingsModule } from '@share-user-account-settings';
import { UserSettingsService } from './development/user-settings.service';
import { environment } from '../environments/environment';
import { ResizableModule } from 'angular-resizable-element';
import { ActivityLogService } from './development/activity-log.service';
import { TermsComponent } from './eula/terms/terms.component';
import { PrivacyComponent } from './eula/privacy/privacy.component';
import { SavedSearchService } from './development/saved-search.service';
import { AppConfig } from './services/app.config';
import { providePublicAuthConfig } from '@auth';
import { PipesModule } from './pipes/pipes.module';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { CommentService } from './services/comment.service';
import {
  WebrtcDebugDialogComponent,
} from './framework/webrtc-player/webrtc-debug-dialog/webrtc-debug-dialog.component';
import { UtilsV2Service } from './services/utils-v2.service';
import { PeopleService } from './development/people.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MatOption, MatRippleModule } from '@angular/material/core';
import { DeveloperGuard } from './guards/developer.guard';
import { LocationEditService } from './development/location-edit.service';
import { LocationComponent } from './pages/location/location.component';
import { FullPageComponent } from './layout/full-page/full-page.component';
import { SharedAlertComponent } from './pages/granted-access/components/shared-alert/shared-alert.component';
import { SharedPanicAlertComponent } from './pages/granted-access/components/shared-panic-alert/shared-panic-alert.component';
import { SharedArchiveComponent } from './pages/granted-access/components/shared-archive/shared-archive.component';
import { InviteUsersModalComponent } from '@modals/invite-users-modal/invite-users-modal.component';
import {
  PendingInvitationsModalComponent,
} from '@modals/pending-invitations-modal/pending-invitations-modal.component';
import { DoorsService } from './development/doors.service';
import { CustomEventsService } from './development/custom-events.service';
import { UserGroupsServices } from './services/user-groups.services';
import { VehiclesService } from './development/vehicles.service';
import { UniformsService } from './development/uniforms.service';
import { CustomObjectsService } from './development/custom-objects.service';
import { MonacoEditorModule, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { PermissionGroupService } from './services/permission-group.service';
import { OrgGroupsService } from './development/org-groups.service';
import { CameraEditGuard } from './guards/camera-edit.guard';
import { RouterPermissionGuard } from './guards/router-permission-guard';
import { CameraCreateGuard } from './guards/camera-create.guard';
import { DashboardService } from './development/dashboard.service';
import {
  LocalStorageService,
  SessionStorageService,
  UiUnusualEventComponent,
  UiCommentFormComponent,
  UiFreeTextSearchComponent,
  UiButtonComponent,
  UiCalendarInlineComponent,
  UiDropdownComponent, UiMotionComponent,
} from '@ui';
import { RbacOrgAllowDirective } from './authorization/rbac-org-allow.directive';
import { PermissionService } from './services/permission.service';
import { WidgetService } from './development/widget.service';
import { LockService } from './development/lock.service';
import {
  BrowserCompatibilityModalComponent,
} from '@modals/browser-compatibility-modal/browser-compatibility-modal.component';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { IdentityService } from './services/identity.service';
import { LivekitService } from './development/livekit.service';
import { SupportService } from './services/support.service';
import { UserDeviceService } from './services/user-device.service';
import { MfaService } from './services/mfa.service';
import { HttpRequestService } from './services/http-request.service';
import { FloorService } from './development/floor.service';
import { WallV2Service } from './services/wall-v2.service';
import { YoutubeService } from './services/youtube.service';
import { AccessDoorsService } from './services/access-doors.service';
import { CamerasSettingsService } from './services/cameras-settings.service';
import { BreadcrumbsService } from './services/breadcrumbs.service';
import { SharedApiService } from './services/shared-api.service';
import { AlarmService } from './services/alarm.service';
import { ScheduleService } from './services/schedule.service';
import { SharedWallComponent } from './pages/granted-access/components/shared-wall/shared-wall.component';
import { SharedLiveviewComponent } from './pages/granted-access/components/shared-liveview/shared-liveview.component';
import { provideUiTranslations } from '@ui';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeHe from '@angular/common/locales/he';

import { CameraPickerInlineComponent } from '@modals/camera-picker-inline/camera-picker-inline.component';
import { Directionality } from '@angular/cdk/bidi';
import { CustomDirectionality } from './services/directionality.service';
import { IfDeveloperDirective } from './directives/if-developer.directive';
import {
  UiHeaderWithBreadcrumbsComponent,
} from './shared/ui-kit/ui-header-with-breadcrumbs/ui-header-with-breadcrumbs.component';
import {
  MatAccordion, MatExpansionModule,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialog, MatDialogClose } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/input';
import { MAT_SELECT_CONFIG, MatSelect } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { EnableEscCloseDirective } from './shared/directives/enable-esc-close.directive';
import { WfEnvRestrictDirective } from './shared/directives/wf-env-restrict.directive';
import {
  UiAccessControlPanelComponent,
} from './shared/ui-kit/ui-access-control-panel/ui-access-control-panel.component';
import { LumanaRouteReuseStrategy } from './route-reuse-strategy';
import { UiSearchComponent } from './shared/ui-kit/ui-search/ui-search.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { UiNoDataComponent, UiAvatarComponent, UiFilterChipComponent } from '@ui';
import { UiEventTypesComponent } from './shared/ui-kit/ui-event-types/ui-event-types.component';
import { UiPeriodComponent } from '@ui';
import { SharedEventComponent } from './pages/granted-access/components/shared-event/shared-event.component';
import { UiVideoPlayerComponent } from './shared/ui-kit/ui-video-player/ui-video-player.component';
import { UiPreloaderComponent } from './shared/ui-kit/ui-preloader/ui-preloader.component';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { filter } from 'rxjs';
import { MicrosoftTeamsService } from './services/microsoft-teams.service';
import { DataFlattenerWorkerService } from './development/data-flattener.worker.service';
import { LoggerService } from './services/logger.service';
import { ContextualLoggerService } from './services/contextual-logger.service';
import { UiV2SelectorComponent } from '@ui-kit/ui-v2-selector/ui-v2-selector.component';
import { MapCustomObjectsService } from './services/map-custom-objects.service';
import { MapService } from './services/map.service';
import { AddCameraWizardModule } from '@modals/add-camera-wizard/add-camera-wizard.module';
import { MonitoringService } from './services/monitoring.service';
import { api, oktaApiUrl, userManagerURL } from '@consts/url.const';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslationService } from './services/translation.service';
import { provideShareAuthSession, sharedAuthenticationInterceptor } from '@share-auth-session';
import {
  WebInvalidSessionHandler,
  WebSharedAuthSessionManager,
} from './authentication/web-shared-auth-session.service';
import { COPILOT_KIT_CONFIG, CopilotKit, CopilotKitConfig } from '@copilotkitnext/angular';
import { ID_TOKEN_KEY, PRIVATE_STORAGE_IMAGE_AUTH } from '@ui';
import { PrivateStorageCredentialsService } from './services/private-storage-credentials.service';
import { IntelligenceToolsService } from './services/intelligence-tools.service';
import { UiFullscreenTargetDirective } from '@ui';

export const initializeApp1 = (appInitService: AppConfig): () => Promise<any> => {
  return (): Promise<any> => appInitService.load();
};

/**
 * The Nest Copilot runtime is mounted at one endpoint and requires its
 * `{ method, params, body }` request envelope.  Its multi-route AG-UI paths
 * (`/agent/:id/run`) are not exposed by the backend.
 */
export const configureCopilotRuntime = (copilotKit: CopilotKit): (() => void) => {
  return () => {
    // CopilotKitCore defaults to the REST transport and connects as soon as a
    // runtime URL is provided. Configure the single-endpoint transport first,
    // then attach the URL so startup performs exactly one discovery request.
    copilotKit.updateRuntime({ runtimeTransport: 'single' });
    copilotKit.updateRuntime({ runtimeUrl: api.intelligence.chat });
  };
};

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

registerLocaleData(localeEn);
registerLocaleData(localeHe);

/**
 * MAGIC FOR CONNECTING MONACO EDITOR
 * DONT TOUCH
 */
const baseHref = document.querySelector('base')?.getAttribute('href') ?? '/';

// absolut URL
const minRoot = new URL('assets/monaco/min/', window.location.origin + baseHref).href;      // .../assets/monaco/min/
const vsForPaths = new URL('vs/', minRoot).href.replace(/\/$/, '');                         // .../assets/monaco/min/vs

const monacoConfig: NgxMonacoEditorConfig = {
  requireConfig: { paths: { vs: vsForPaths } },

  onMonacoLoad: () => {
    (window as any).MonacoEnvironment = {
      getWorkerUrl() {
        // Воркеру даём baseUrl = .../min/ (без 'vs'), чтобы внутри он формировал min/ + 'vs/...'
        const blob = new Blob(
          [
            `self.MonacoEnvironment={baseUrl:'${minRoot}'};`,
            `importScripts('${minRoot}vs/base/worker/workerMain.js');`,
          ],
          { type: 'text/javascript' },
        );
        return URL.createObjectURL(blob);
      },
    };
  },
};

@NgModule({
  declarations: [
    AppComponent,
    SplashComponent,
    NoAuthComponent,
    GrantedAccessComponent,
    TermsComponent,
    PrivacyComponent,
    WebrtcDebugDialogComponent,
    LocationComponent,
    FullPageComponent,
    SharedAlertComponent,
    SharedArchiveComponent,
    SharedLiveviewComponent,
    InviteUsersModalComponent,
    PendingInvitationsModalComponent,
    BrowserCompatibilityModalComponent,
  ],
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
      /**
       * IMPORTANT TO KEEP ALL TRANSLATIONS WITH LIBS
       */
      extend: true,
    }),
    BrowserModule,
    AppStoreModule,
    AppRoutingModule, // routings available
    AuthorizationModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    SharedModule,
    StoreRouterConnectingModule.forRoot(),
    LazyLoadImageModule,
    LetDirective,
    PushPipe,
    UiKitModule,
    UiFullscreenTargetDirective,
    MonacoEditorModule.forRoot(monacoConfig),
    AppSettingsModule,
    ResizableModule,
    PipesModule,
    NgScrollbarModule,
    ServiceWorkerModule.register('ngsw-custom-worker.js', {
      enabled: true,
      //   enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    MatRippleModule,
    UiDropdownComponent,
    RbacOrgAllowDirective,
    SharedWallComponent,
    CameraPickerInlineComponent,
    IfDeveloperDirective,
    UiHeaderWithBreadcrumbsComponent,
    MatExpansionModule,
    MatAccordion,
    MatExpansionPanelDescription,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatIcon,
    MatTooltip,
    MatSlider,
    MatSliderThumb,
    ScrollingModule,
    MatDialogClose,
    MatLabel,
    MatSelect,
    MatOption,
    MatCheckbox,
    EnableEscCloseDirective,
    WfEnvRestrictDirective,
    UiAccessControlPanelComponent,
    UiSearchComponent,
    UiCalendarInlineComponent,
    UiNoDataComponent,
    UiUnusualEventComponent,
    UiMotionComponent,
    UiEventTypesComponent,
    UiPeriodComponent,
    UiFilterChipComponent,
    SharedEventComponent,
    SharedPanicAlertComponent,
    UiCommentFormComponent,
    UiVideoPlayerComponent,
    UiButtonComponent,
    UiPreloaderComponent,
    UiFreeTextSearchComponent,
    UiAvatarComponent,
    MatRadioGroup,
    MatRadioButton,
    UiV2SelectorComponent,
    UiDatasetTableComponent,
    AddCameraWizardModule,
    ShareUserAccountSettingsModule.forRoot({ apiUrl: environment.apiLumana, userManagerUrl: userManagerURL }),
  ],
  providers: [
    AppConfig,
    DatePipe,
    ...provideUiTranslations(),
    ...provideShareOrganizationSettings(),
    ...provideShareAuthSession({
      sessionManager: WebSharedAuthSessionManager,
      invalidSessionHandler: WebInvalidSessionHandler,
    }),
    { provide: APP_INITIALIZER, useFactory: initializeApp1, deps: [AppConfig], multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: configureCopilotRuntime,
      deps: [CopilotKit],
      multi: true,
    },
    providePublicAuthConfig({
      auth0Domain: environment.auth0.domain,
      auth0ClientId: environment.auth0.clientID,
      authStartUrl: api.authV2.login,
      logoutPublicUrl: api.authV2.logoutPublic,
      resendVerificationEmailPublicUrl: api.authV2.resendVerificationEmailPublic,
    }),
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) => translateService.currentLang || 'en-US',
    },
    { provide: Window, useValue: window },
    provideHttpClient(withInterceptors([sharedAuthenticationInterceptor])),
    {
      provide: PRIVATE_STORAGE_IMAGE_AUTH,
      useExisting: PrivateStorageCredentialsService,
    },
    {
      provide: COPILOT_KIT_CONFIG,
      useFactory: (
        localStorageService: LocalStorageService,
        toolsService: IntelligenceToolsService,
      ): CopilotKitConfig => {
        // Dynamic getter evaluated fresh on every request so the token is never stale.
        const headers = {} as Record<string, string>;
        Object.defineProperty(headers, 'Authorization', {
          get: () => `Bearer ${localStorageService.getItemWithCheck(ID_TOKEN_KEY) ?? ''}`,
          enumerable: true,
          configurable: true,
        });

        return {
          headers,
          // Pattern A tools (preview panel). Pattern B (backend actions) are in ChatService.
          frontendTools: toolsService.getFrontendTools(),
        };
      },
      deps: [LocalStorageService, IntelligenceToolsService],
    },
    { provide: Directionality, useExisting: CustomDirectionality },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        disableClose: true,
        hasBackdrop: true,
        backdropClass: 'dialog-backdrop',
        closeOnNavigation: true,
      },
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        panelWidth: 'auto',
        overlayPanelClass: 'mat-select-autosize-panel',
      },
    },
    provideAppInitializer(() => {
      const router = inject(Router);
      const dialog = inject(MatDialog);

      router.events
        .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
        .subscribe(() => dialog.closeAll());

      return;
    }),
    { provide: RouteReuseStrategy, useClass: LumanaRouteReuseStrategy },
    OrganizationService,
    SharedService,
    AlertEventsService,
    SavedSearchService,
    SearchConfigurationService,
    AlertMonitoringService,
    CameraService,
    CameraSearchSettingsService,
    GrantedAccessService,
    LocationEditService,
    StatsService,
    UserSettingsService,
    ActivityLogService,
    CommentService,
    UtilsV2Service,
    PeopleService,
    DeveloperGuard,
    DoorsService,
    VehiclesService,
    UniformsService,
    CustomObjectsService,
    CustomEventsService,
    UserGroupsServices,
    OrgGroupsService,
    PermissionGroupService,
    CameraEditGuard,
    RouterPermissionGuard,
    CameraCreateGuard,
    DashboardService,
    PermissionService,
    WidgetService,
    LockService,
    IdentityService,
    LivekitService,
    SupportService,
    UserDeviceService,
    MfaService,
    HttpRequestService,
    MicrosoftTeamsService,
    FloorService,
    WallV2Service,
    YoutubeService,
    SharedApiService,
    AccessDoorsService,
    CamerasSettingsService,
    BreadcrumbsService,
    AlarmService,
    ScheduleService,
    MapCustomObjectsService,
    MapService,
    MonitoringService,
  ],
  bootstrap: [AppComponent],
  exports: [LocationComponent],
})
export class AppModule {
  constructor(public translateService: TranslationService) {
    this.translateService.initLanguage();
  }
}
